import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import type { User } from '@/types';

export interface AdminSummary {
  id: number;
  email: string;
  first_name?: string | null;
  last_name?: string | null;
  role: string;
  is_active: boolean;
  is_email_verified?: boolean;
  max_users_quota?: number | null;
  users_count: number;
  mobile_phone?: string | null;
  company_name?: string | null;
  siret?: string | null;
  address?: string | null;
  city?: string | null;
  postal_code?: string | null;
  country?: string | null;
  maps_link?: string | null;
  commissions_enabled?: string | null;
  bank_name?: string | null;
  bank_account_holder?: string | null;
  iban?: string | null;
  bic?: string | null;
  marques?: string[] | null;  // Marques assignées (pour apporteurs)
  last_login?: string | null;
}

export interface CreateAdminPayload {
  email: string;
  password?: string;
  first_name?: string;
  last_name?: string;
  role?: string;
  mobile_phone?: string;
  company_name?: string;
  siret?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  maps_link?: string;
  commissions_enabled?: string;
  is_active?: boolean;
  bank_name?: string;
  bank_account_holder?: string;
  iban?: string;
  bic?: string;
  marques?: string[];  // Marques assignées (pour apporteurs)
}

export interface UpdateAdminPayload extends Partial<CreateAdminPayload> {
  admin_id: number;
}

export interface UpdateAdminQuotaPayload {
  admin_id: number;
  max_users_quota?: number | null;
}

export interface AdminUsersSummary {
  users_count: number;
  max_users_quota: number;
  remaining: number;
  users: User[];
}

export interface CreateSimpleUserPayload {
  email: string;
  password: string;
  first_name?: string;
  last_name?: string;
  can_delete?: boolean;
}

// ============================
// Super Admin: manage admin users
// ============================

export const useSuperAdminListAdminUsers = (adminId: number) => {
  return useQuery({
    queryKey: ['rbac', 'admin-users', adminId],
    queryFn: async (): Promise<AdminUsersSummary> => {
      const res = await api.get(`/users/admins/${adminId}/users`);
      return res.data;
    },
    enabled: !!adminId,
    staleTime: 30_000,
  });
};

export const useSuperAdminCreateUser = (adminId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSimpleUserPayload): Promise<User> => {
      const res = await api.post(`/users/admins/${adminId}/users`, payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'admin-users', adminId]);
      qc.invalidateQueries(['rbac', 'admins']);
    },
  });
};

// Super Admin: update admin profile
export const useUpdateAdmin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ admin_id, ...data }: UpdateAdminPayload): Promise<AdminSummary> => {
      const res = await api.patch(`/users/admins/${admin_id}`, data);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'admins']);
    },
  });
};

// Super Admin: update admin email verification status
export const useUpdateAdminEmailVerification = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ admin_id, is_email_verified }: { admin_id: number; is_email_verified: boolean }): Promise<AdminSummary> => {
      const res = await api.patch(`/users/admins/${admin_id}/email-verification`, { is_email_verified });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'admins']);
    },
  });
};

// Super Admin: update user email verification status
export const useUpdateUserEmailVerification = (adminId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ user_id, is_email_verified }: { user_id: number; is_email_verified: boolean }): Promise<User> => {
      const res = await api.patch(`/users/admins/${adminId}/users/${user_id}/email-verification`, { is_email_verified });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'admin-users', adminId]);
    },
  });
};

export const useSuperAdminDeleteUser = (adminId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (userId: number): Promise<void> => {
      await api.delete(`/users/admins/${adminId}/users/${userId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'admin-users', adminId]);
      qc.invalidateQueries(['rbac', 'admins']);
    },
  });
};

export interface SuperAdminUserPermission {
  user_id: number;
  can_delete: boolean;
}

export const useSuperAdminGetUserPermissions = (adminId: number, userId: number) => {
  return useQuery({
    queryKey: ['rbac', 'sa-user-perms', adminId, userId],
    queryFn: async (): Promise<SuperAdminUserPermission> => {
      const res = await api.get(`/users/admins/${adminId}/users/${userId}/permissions`);
      return res.data;
    },
    enabled: !!adminId && !!userId,
    staleTime: 30_000,
  });
};

export const useSuperAdminUpdateUserPermissions = (adminId: number) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ user_id, can_delete }: { user_id: number; can_delete: boolean }): Promise<SuperAdminUserPermission> => {
      const res = await api.put(`/users/admins/${adminId}/users/${user_id}/permissions`, { can_delete });
      return res.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries(['rbac', 'sa-user-perms', adminId, variables.user_id]);
      qc.invalidateQueries(['rbac', 'admin-users', adminId]);
    },
  });
};

// Super Admin: list admins
export interface AdminListFilters {
  search?: string;
  status?: 'all' | 'active' | 'inactive';
  commissions?: 'all' | 'with' | 'without';
  role?: 'admin' | 'apporteur' | 'super_admin';
}

export const useListAdmins = (filters?: AdminListFilters) => {
  const search = filters?.search;
  const status = filters?.status ?? 'all';
  const commissions = filters?.commissions ?? 'all';
  const role = filters?.role;

  return useQuery({
    queryKey: ['rbac', 'admins', search || '', status, commissions, role || ''],
    queryFn: async (): Promise<AdminSummary[]> => {
      const params: Record<string, any> = {};

      if (search) {
        params.search = search;
      }
      if (status === 'active') {
        params.is_active = true;
      } else if (status === 'inactive') {
        params.is_active = false;
      }

      if (commissions === 'with') {
        params.commissions_enabled = 'with';
      } else if (commissions === 'without') {
        params.commissions_enabled = 'without';
      }

      if (role) {
        params.role_filter = role;
      }

      const res = await api.get('/users/admins', {
        params: Object.keys(params).length ? params : undefined,
      });
      return res.data;
    },
    staleTime: 60_000,
  });
};

// Super Admin: create new admin
export const useCreateAdmin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateAdminPayload): Promise<AdminSummary> => {
      const res = await api.post('/users/admins', payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'admins']);
    },
  });
};

// Super Admin: update admin quota
export const useUpdateAdminQuota = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ admin_id, max_users_quota }: UpdateAdminQuotaPayload): Promise<AdminSummary> => {
      const res = await api.put(`/users/admins/${admin_id}/quota`, { max_users_quota });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'admins']);
    },
  });
};

// Super Admin: delete admin (only if no users attached)
export const useDeleteAdmin = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (admin_id: number): Promise<void> => {
      await api.delete(`/users/admins/${admin_id}`);
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'admins']);
    },
  });
};

// Super Admin: batch delete admins
export const useBatchDeleteAdmins = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (admin_ids: number[]): Promise<{ success: boolean; deleted_count: number; failed_ids: number[]; message: string }> => {
      const res = await api.post('/users/admins/batch-delete', { ids: admin_ids });
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'admins']);
    },
  });
};

// Admin: list my users summary
export const useAdminUsersSummary = () => {
  return useQuery({
    queryKey: ['rbac', 'my-users'],
    queryFn: async (): Promise<AdminUsersSummary> => {
      const res = await api.get('/users/my/users');
      return res.data;
    },
    staleTime: 30_000,
  });
};

// Admin: create a simple user within quota
export const useAdminCreateUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateSimpleUserPayload): Promise<User> => {
      const res = await api.post('/users/my/users', payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries(['rbac', 'my-users']);
    },
  });
};

// Admin: user permissions
export interface AdminUserPermission {
  user_id: number;
  can_delete: boolean;
}

export const useAdminGetUserPermissions = (userId: number) => {
  return useQuery({
    queryKey: ['rbac', 'user-perms', userId],
    queryFn: async (): Promise<AdminUserPermission> => {
      const res = await api.get(`/users/my/users/${userId}/permissions`);
      return res.data;
    },
    enabled: !!userId,
    staleTime: 30_000,
  });
};

export const useAdminUpdateUserPermissions = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ user_id, can_delete }: { user_id: number; can_delete: boolean }): Promise<AdminUserPermission> => {
      const res = await api.put(`/users/my/users/${user_id}/permissions`, { can_delete });
      return res.data;
    },
    onSuccess: (_data, variables) => {
      qc.invalidateQueries(['rbac', 'user-perms', variables.user_id]);
    },
  });
};

// Admin: reset user password
export const useAdminResetUserPassword = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ user_id, new_password }: { user_id: number; new_password: string }): Promise<{ success: boolean; message: string }> => {
      const res = await api.post(`/users/my/users/${user_id}/password`, { new_password });
      return res.data;
    },
    onSuccess: (_data, variables) => {
      // No specific cache to invalidate; keep for symmetry
      qc.invalidateQueries(['rbac', 'user-perms', variables.user_id]);
    },
  });
};

// Current user: my permissions
export interface MyPermissions {
  can_delete: boolean;
}

export const useMyPermissions = () => {
  return useQuery({
    queryKey: ['rbac', 'me-permissions'],
    queryFn: async (): Promise<MyPermissions> => {
      const res = await api.get('/users/me/permissions');
      return res.data;
    },
    staleTime: 30_000,
  });
};
